import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * NORTH GALLERY (Северная галерея) — a grand processional CLOISTER: the colonnaded approach that leads the
 * eye south to the throne. The hero is the strict SYMMETRY — two stately rows of PILLARS march the length of
 * the hall, a royal RUNNER draws the ceremonial axis down the centre, and the broad central lane is left
 * deliberately EMPTY (the negative space IS the grandeur). Heraldic BANNERS hang high between the columns,
 * sentinel SUITS OF ARMOUR keep silent watch at the side walls, and warm light pools (lampposts on the north
 * row, candelabra flanking the benches) carry the eye toward the throne. Airy, sparse, stately — no clutter.
 *
 * Geometry: zone (1180,360) 1240×400 → centre (1800,560). The N/S door axis (x1800) and the W/E door axis
 * (y560) stay walkable; the two pillar rows sit at y440/y680 so the central lane between them is never blocked.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;
  const cx = 1800;
  const cy = 560;

  // ── Floor: the ceremonial runner laid the length of the gallery (first, under everything). ──
  place(ctx, kit.runnerRug(art, 900, 90), cx, cy);

  // ── Two rows of colonnade PILLARS (the hero), framing the central processional lane; centre gap left
  //    clear so the N/S door axis stays open. North row at y440, south row at y680. ──
  const pillarXs = [1320, 1560, 2040, 2280] as const;
  for (const px of pillarXs) {
    place(ctx, kit.pillar(art), px, 440); // north colonnade
    place(ctx, kit.pillar(art), px, 680); // south colonnade
  }

  // ── Heraldic BANNERS strung high between the columns (varied colours), symmetric on the long walls. ──
  place(ctx, kit.banner(art, undefined, 110), 1440, 392); // north wall, gold
  place(ctx, kit.banner(art, 0x2f4a8a, 110), 2160, 392); // north wall, royal blue
  place(ctx, kit.banner(art, 0x8a2f2f, 110), 1440, 728); // south wall, crimson
  place(ctx, kit.banner(art, undefined, 110), 2160, 728); // south wall, gold

  // ── Gallery PEW benches at the south-row outer pillars (load-bearing seats), a candelabra beside each
  //    casting a warm pool over the seat — on-narrative stone-and-flame, balances the north-row lamps. ──
  place(ctx, kit.pew(art, 120), 1340, 680);
  place(ctx, kit.candelabra(art), 1440, 690);
  place(ctx, kit.pew(art, 120), 2260, 680);
  place(ctx, kit.candelabra(art), 2160, 690);

  // ── LAMPPOSTS at the north-row outer pillars — warm light pooling over the colonnade approach. ──
  place(ctx, g.lamppost(art), 1340, 440);
  place(ctx, g.lamppost(art), 2260, 440);

  // ── Sentinel SUITS OF ARMOUR standing watch at the W and E side walls (inset off the door mouths). ──
  place(ctx, kit.suitOfArmour(art), 1300, 560);
  place(ctx, kit.suitOfArmour(art), 2300, 560);
};

export default decorate;
